'use client'

import {
  type IBasicUser,
  type IChatLiteMessage,
  type IChatResponse,
  usePusher,
  useQueryContext,
  useSession,
} from '@repo/sdk'
import { Message, Spinner } from '@repo/ui'
import { cn } from '@repo/ui/lib'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { MessagesSquare } from 'lucide-react'
import Image from 'next/image'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

const EmptyMessage = () => {
  const [clickCount, setClickCount] = useState(0)

  return (
    <>
      <div className="w-100 flex h-full flex-col items-center justify-center gap-3">
        <div className={'rounded-full bg-slate-100 p-2'}>
          <MessagesSquare
            className="p-2 text-slate-500"
            onClick={() => {
              setClickCount(clickCount + 1)
            }}
            size={60}
            strokeWidth={1.5}
          />
        </div>
        <p className="font-semibold">No messages yet.</p>
        <p className={'max-w-[275px] text-center text-gray-600'} style={{ textWrap: 'balance' }}>
          Start a conversation about your shipment with us now!
        </p>
      </div>
      <div
        className={`fixed bottom-0 left-0 transition-transform duration-500 ${
          clickCount > 4 ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <Image
          alt={'easter egg'}
          className={'pointer-events-none'}
          height={1000}
          src={'/easter-egg.png'}
          width={1000}
        />
      </div>
    </>
  )
}

const groupMessagesByDate = (messages: (IChatLiteMessage | undefined)[]) => {
  return messages.reduce<Record<string, IChatLiteMessage[]>>((acc, message) => {
    if (!message) return acc

    const date = new Date(message.created_at).toLocaleDateString()
    acc[date] ||= []
    acc[date].push(message)
    return acc
  }, {})
}

type IProps = ReturnType<typeof useInfiniteChatMessages> & {
  className?: string
  markMessagesAsRead: () => void
}

export const Chat = ({
  className,
  fetchNextPage,
  flattenMessages,
  groupedMessages,
  hasNextPage,
  isLoading,
  markMessagesAsRead,
  userData,
}: IProps) => {
  const { ref } = useLoadMoreMessages({ fetchNextPage })

  const { scrollContainerRef } = useScrollToBottom({ flattenMessages })

  useEffect(() => {
    if (!isLoading) markMessagesAsRead()
  }, [isLoading])

  return (
    <div
      className={cn('relative flex h-full flex-col gap-4 overflow-y-auto bg-card pt-2', className)}
      ref={scrollContainerRef}
    >
      {(isLoading || hasNextPage || !userData) && (
        <div className={'flex w-full items-center justify-center'} ref={ref}>
          <Spinner className={'color-primary h-[30px] w-[30px]'} />
        </div>
      )}
      {!isLoading && !flattenMessages[0] && <EmptyMessage />}
      {!isLoading && flattenMessages[0] && userData && (
        <div className="flex h-auto flex-col-reverse gap-1 px-4">
          {Object.keys(groupedMessages).map((date, outerIndex) => (
            <div className="flex h-auto flex-col gap-1" key={date}>
              <p className="my-4 text-center text-xs text-gray-500">{date}</p>
              <div className="flex h-auto flex-col-reverse gap-1 px-4">
                {groupedMessages[date].map((message, index) => {
                  const prevMessage = groupedMessages[date][index - 1]
                  return (
                    <Fragment key={`${message.id}-${index}`}>
                      {outerIndex === Object.keys(groupedMessages).length - 1 &&
                        index === groupedMessages[date].length - 1 &&
                        userData.role?.name == 'shipper' && (
                          <Message
                            message={{
                              ...message,
                              message:
                                'Hi, we have received your message and will get back to you as soon as possible.',
                            }}
                            prevMessage={prevMessage}
                            userData={{}}
                          />
                        )}

                      <Message message={message} prevMessage={prevMessage} userData={userData} />
                    </Fragment>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export const useInfiniteChatMessages = ({
  chatId,
  fetch,
  isAdmin = false,
  loadId,
  userData,
}: {
  chatId: number | undefined
  fetch: (page: number) => Promise<IChatResponse | undefined>
  isAdmin?: boolean
  loadId: number | undefined
  userData?: Partial<IBasicUser>
}) => {
  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    initialPageParam: 1,
    //eslint-disable-next-line perfectionist/sort-objects
    queryFn: async ({ pageParam }) => {
      return await fetch(pageParam)
    },
    //eslint-disable-next-line perfectionist/sort-objects
    queryKey: [`chat-${chatId}`, `user-${userData?.id}`, `load-${loadId}`, `isAdmin-${isAdmin}`],
    //eslint-disable-next-line perfectionist/sort-objects
    getNextPageParam: lastPage =>
      !lastPage || lastPage?.meta.current_page === lastPage?.meta.last_page
        ? undefined
        : lastPage.meta.current_page + 1,
  })

  const flattenMessages = useMemo(() => {
    if (!data?.pages) return []

    return data.pages.flatMap(page => page?.data)
  }, [data])

  const groupedMessages = useMemo(() => {
    return groupMessagesByDate(flattenMessages)
  }, [flattenMessages])

  return {
    fetchNextPage,
    flattenMessages,
    groupedMessages,
    hasNextPage,
    isLoading,
    userData,
  }
}

const useLoadMoreMessages = ({
  fetchNextPage,
}: Pick<ReturnType<typeof useInfiniteChatMessages>, 'fetchNextPage'>) => {
  const { inView, ref } = useInView()

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  return { ref }
}

const useScrollToBottom = ({
  flattenMessages,
}: Pick<ReturnType<typeof useInfiniteChatMessages>, 'flattenMessages'>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Function to scroll the container to the bottom
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    // Scroll after messages are potentially rendered
    // Using setTimeout ensures the scroll happens after the DOM update cycle
    const timer = setTimeout(() => {
      scrollToBottom()
    }, 0) // Delay of 0ms is often enough

    return () => clearTimeout(timer) // Cleanup timeout on unmount or before next effect run
  }, [flattenMessages]) // Dependency: run when flattenMessages array changes

  return {
    scrollContainerRef,
  }
}

export const useSubscribeToChatChannel = ({
  channel,
  chatId,
  cleanUpCallback,
  event,
  eventCallback,
  isAdmin = false,
  loadId,
}: {
  channel: string
  chatId: number
  cleanUpCallback?: () => void
  event: string
  eventCallback?: () => Promise<void>
  isAdmin?: boolean
  loadId: number
}) => {
  const queryContext = useQueryContext()
  const session = useSession()
  const queryClient = useQueryClient()
  const { pusher } = usePusher()

  const userId = session?.data?.user?.id

  useEffect(() => {
    const init = async () => {
      eventCallback?.()

      if (chatId && userId) {
        const chatChannel = pusher.subscribe(channel)

        chatChannel.bind(event, async () => {
          queryContext.refetch()
          await queryClient.invalidateQueries({
            queryKey: [`chat-${chatId}`, `user-${userId}`, `load-${loadId}`, `isAdmin-${isAdmin}`],
          })
        })
      }
    }

    if (chatId && userId) {
      init()

      return () => {
        pusher?.unsubscribe(channel)
        cleanUpCallback?.()
      }
    }
  }, [pusher, loadId, session])
}
